import { ibmPlexMono, ibmPlexSans } from "configuration/fonts"
import type { AppProps as NextJsAppProps } from "next/app"
import { appWithTranslation } from "next-i18next"
import { useEffect, useMemo } from "react"
import { StorageProvider } from "storage"
import { SystemsManager } from "features/systems-management"
import nextI18nConfig from "../../next-i18next.config"
// eslint-disable-next-line import/no-unresolved -- public import that next.js recognizes
import "/styles/globals.css"
import "external-svg-loader"
import PageErrorBoundary from "error-boundary/PageErrorBoundary"
import { setupBrowserTelemetry } from "@nova/telemetry-browser"
import { setBackend, setRootTrace } from "@nova/telemetry"
import Head from "next/head"
import { getAppTitle } from "tools/browser/getAppTitle"
import Script from "next/script"
import { useTranslation } from "@nova/use-translation"
import { datadogRum, Site } from "@datadog/browser-rum"
import Package from "../../package.json"
import { config } from "@nova/configuration/browser"
import { useAppSelector } from "integrations/redux/hooks"
import { selectActivatedFire } from "features/map/control/selectors"
import { installTaskWorkers } from "integrations/webworker-pool"

let doonce = false
function setupClientTelemetry() {
    if (doonce) return
    doonce = true
    const { log, metric } = setupBrowserTelemetry(
        "/api/collector",
        10000,
        window.navigator,
        window.document
    )
    setBackend(log, metric)
    setRootTrace("client")
    if (config.get("environmentName") === "localhost" && !config.get("devMetrics")) return
    datadogRum.init({
        applicationId: config.get("ddAppId") || "",
        clientToken: config.get("ddClientToken") || "",
        site: (config.get("ddSite") as Site) || "us3.datadoghq.com",
        service: "nova-web-application",
        env: config.get("environmentName") || "env not set",
        version: Package.version,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 80,
        trackUserInteractions: true,
        trackResources: true,
        compressIntakeRequests: true,
        trackLongTasks: true,
        defaultPrivacyLevel: "mask-user-input",
        traceSampleRate: 20,
    })
    datadogRum.startSessionReplayRecording()
    return
}

export function PageTitle({
    publicEnvironment,
}: {
    publicEnvironment: Record<string, string | undefined>
}) {
    const appTitle = getAppTitle(publicEnvironment.NEXT_PUBLIC_NOVA_ENVIRONMENT_NAME)
    const { t } = useTranslation()
    const activeProject = useAppSelector(selectActivatedFire)
    const title = useMemo(
        () => `${t(appTitle)}${activeProject ? ` - ${activeProject.name}` : ""}`,
        [t, appTitle, activeProject]
    )
    return (
        // the rule targets a custom _document; one <Head> per _app is what Next expects
        // oxlint-disable-next-line next/no-duplicate-head
        <Head>
            <title>{title}</title>
            <meta property="og:title" content={title} key="title" />
        </Head>
    )
}

const App = ({
    Component: PageComponent,
    pageProps,
    publicEnvironment,
}: NextJsAppProps & { publicEnvironment: Record<string, string | undefined> }) => {
    useEffect(setupClientTelemetry, [])
    useEffect(installTaskWorkers, [])
    publicEnvironment = pageProps?.publicEnvironment || publicEnvironment
    const GA_TAG = publicEnvironment.NEXT_PUBLIC_NOVA_GA_MEASUREMENT_ID || ""
    return (
        <>
            <Head>
                <link
                    rel="icon"
                    href="/favicon.ico"
                    media="(prefers-color-scheme: no-preference)"
                />
                <link rel="icon" href="/favicon-dark.svg" media="(prefers-color-scheme: dark)" />
                <link rel="icon" href="/favicon-light.svg" media="(prefers-color-scheme: light)" />
            </Head>
            <Script
                src={`https://www.googletagmanager.com/gtag/js?id=${GA_TAG}`}
                strategy="afterInteractive"
            />
            <Script id="google-analytics" strategy="afterInteractive">
                {`
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${GA_TAG}');
                `}
            </Script>
            <Script id="data-environment-variables" type="application/json">
                {JSON.stringify(publicEnvironment)}
            </Script>
            <div
                className={`${ibmPlexSans.variable} ${ibmPlexMono.variable} ${ibmPlexSans.className}`}
            >
                <PageErrorBoundary>
                    <StorageProvider>
                        <PageTitle publicEnvironment={publicEnvironment} />
                        <SystemsManager>
                            <PageComponent {...pageProps} />
                        </SystemsManager>
                    </StorageProvider>
                </PageErrorBoundary>
            </div>
        </>
    )
}

App.getInitialProps = () => {
    const bag = {} as Record<string, string | undefined>
    for (const p in process.env) {
        if (p.startsWith("NEXT_PUBLIC")) {
            bag[p] = process.env[p]
        }
    }
    return { publicEnvironment: bag }
}

export default appWithTranslation(App, nextI18nConfig)
